import React, {useCallback, useMemo, useState} from 'react';
import {Box, Divider, Stack} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Body,
  Input,
  designSystemToken,
  Label,
} from '@lightricks/react-design-system';
import Sentry from '@/services/sentry/Sentry';
import formatCentsToDollars from '@/utils/formatCentsToDollars';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {
  DELIVERABLE_KINDS,
  getDeliverableKindLabel,
} from '@/config/deliverableKinds';
import Checkbox from '@/components/checkbox';
import FlashMessage from '@/components/flash-message';
import useCreateBrandNegotiation from '@/hooks/mutations/use-create-brand-negotiation';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import usePaidGigQuery from '@/hooks/queries/use-paid-gig-query';
import styles from './NegotiateModal.module.scss';
import classifyNegotiationError from './classifyNegotiationError';

const MAX_TIME_PERIOD_MONTHS = 36;

function ComparisonRow({
  newLabel,
  oldLabel,
  newValue,
  oldValue,
  onValueChange,
  inputError,
  integerOnly = false,
}: {
  newLabel: string;
  oldLabel: string;
  newValue: number | null;
  oldValue: number | null;
  onValueChange: (text: string) => void;
  inputError?: string | null;
  integerOnly?: boolean;
}) {
  const blockedKeys = integerOnly
    ? ['-', '+', 'e', 'E', '.']
    : ['-', '+', 'e', 'E'];
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack alignItems="flex-start">
        <Label size="sm" color={designSystemToken('semantic.fg.primary')}>
          {newLabel}
        </Label>
        <Input
          type="number"
          value={newValue}
          onInputChange={onValueChange}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (blockedKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
          clearIcon={false}
          sx={{width: '245px'}}
        />
        {inputError ? (
          <Body
            size="xs"
            color={designSystemToken('semantic.fg.danger')}
            sx={{marginTop: '-4px'}}
          >
            {inputError}
          </Body>
        ) : null}
      </Stack>
      <Stack />
      <Stack alignItems="flex-start">
        <Body size="sm" color={designSystemToken('semantic.fg.disabled')}>
          {oldLabel}
        </Body>
        <Input
          type="number"
          value={oldValue}
          disabled
          clearIcon={false}
          sx={{width: '245px'}}
        />
      </Stack>
    </Stack>
  );
}

type DeliverableField = keyof typeof DELIVERABLE_KINDS;

const STORY_FRAMES_FIELD: DeliverableField = 'minimumNumberOfMixedMedia';
const STORY_UPLOADS_FIELD: DeliverableField = 'mixedMediaToUpload';
const MIN_STORY_FRAMES = 2;

const DELIVERABLE_FIELDS = (
  Object.keys(DELIVERABLE_KINDS) as DeliverableField[]
).filter((f) => f !== STORY_FRAMES_FIELD);

// Mirrors Ember's _isAllowedForCampaignNetwork: Instagram allows all chips;
// non-Instagram networks hide Reels and Story Posts; content-creation campaigns
// (null network) additionally hide Feed Posts.
// Only applies to the inactive chip list — active deliverables are always shown.
function getAllowedChipFields(
  networkPrettyName: string | null | undefined
): Set<DeliverableField> {
  const all = new Set(DELIVERABLE_FIELDS);
  if (networkPrettyName === undefined || networkPrettyName === 'Instagram') {
    return all;
  }
  all.delete('requiredReelsPostsCount');
  all.delete('requiredStoryPostsCount');
  if (networkPrettyName === null) {
    all.delete('requiredFeedPostsCount');
  }
  return all;
}

function getDeliverableLabel(
  field: DeliverableField,
  networkPrettyName: string | null | undefined
): string {
  return getDeliverableKindLabel(DELIVERABLE_KINDS[field], networkPrettyName);
}

const ALL_DELIVERABLE_FIELDS: DeliverableField[] = [
  ...DELIVERABLE_FIELDS,
  STORY_FRAMES_FIELD,
];

interface NegotiateModalContentProps {
  isSubmitting: boolean;
  effectiveOfferCents: number;
  currentOfferCents: number;
  totalWithFees: number | null;
  activeDeliverables: DeliverableField[];
  inactiveDeliverables: DeliverableField[];
  effectiveDeliverables: Record<DeliverableField, number>;
  hasPendingOffer: boolean;
  acknowledged: boolean;
  canSubmit: boolean;
  effectiveTimePeriod: number | null;
  currentTimePeriod: number | null;
  timePeriodError: string | null;
  networkPrettyName: string | null | undefined;
  onOfferChange: (value: string) => void;
  storyFramesCount: number;
  onDeliverableChange: (field: DeliverableField, delta: number) => void;
  onTimePeriodChange: (value: string) => void;
  onAcknowledgeToggle: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

function NegotiateModalContent({
  isSubmitting,
  effectiveOfferCents,
  currentOfferCents,
  totalWithFees,
  activeDeliverables,
  inactiveDeliverables,
  effectiveDeliverables,
  storyFramesCount,
  networkPrettyName,
  hasPendingOffer,
  acknowledged,
  canSubmit,
  effectiveTimePeriod,
  currentTimePeriod,
  timePeriodError,
  onOfferChange,
  onDeliverableChange,
  onTimePeriodChange,
  onAcknowledgeToggle,
  onSubmit,
  onClose,
}: NegotiateModalContentProps) {
  return (
    <Stack gap="16px" className={styles.modalContent}>
      <Stack alignItems="flex-start" gap="16px">
        <Headline size="md">{translate('Send a revised offer')}</Headline>
        <Body
          size="md"
          color={designSystemToken('semantic.fg.secondary')}
          textAlign="left"
        >
          {translate('Review and update the details before sending.')}
        </Body>
      </Stack>
      <ComparisonRow
        key={`offer-${currentOfferCents}`}
        newLabel={translate('New Offer')}
        oldLabel={translate('Original Offer')}
        newValue={effectiveOfferCents / 100}
        oldValue={currentOfferCents / 100}
        onValueChange={onOfferChange}
      />

      <Stack
        gap="4px"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        border={`1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`}
        borderRadius="8px"
        padding="12px"
      >
        <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
          {translate('Total Cost w/Fees')}
        </Body>
        <Body size="md">
          {totalWithFees === null ? '—' : formatCentsToDollars(totalWithFees)}
        </Body>
      </Stack>
      <Divider />
      <Label
        size="lg"
        color={designSystemToken('semantic.fg.secondary')}
        sx={{alignSelf: 'flex-start'}}
      >
        {translate('Usage Rights Period (months)')}
      </Label>
      <ComparisonRow
        key={`usage-${currentTimePeriod}`}
        newLabel={translate('New')}
        oldLabel={translate('Current')}
        newValue={effectiveTimePeriod}
        oldValue={currentTimePeriod}
        onValueChange={onTimePeriodChange}
        inputError={timePeriodError}
        integerOnly
      />

      <Box className={styles.divider} />
      <Stack gap="16px" alignItems="flex-start">
        <Label size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {translate('+ Add deliverable')}
        </Label>
        {activeDeliverables.flatMap((field) => {
          const row = (
            <Stack
              key={field}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className={styles.deliverableRow}
            >
              <Body size="lg">
                {getDeliverableLabel(field, networkPrettyName)}
              </Body>
              <Stack
                direction="row"
                alignItems="center"
                className={styles.counterGroup}
              >
                <Stack
                  component="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  className={styles.counterButton}
                  onClick={() => onDeliverableChange(field, -1)}
                  disabled={effectiveDeliverables[field] <= 0}
                >
                  −
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  className={styles.counterValue}
                >
                  <Body size="lg">{effectiveDeliverables[field]}</Body>
                </Stack>
                <Stack
                  component="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  className={styles.counterButton}
                  onClick={() => onDeliverableChange(field, 1)}
                >
                  +
                </Stack>
              </Stack>
            </Stack>
          );

          if (field !== STORY_UPLOADS_FIELD) {
            return [row];
          }

          return [
            row,
            <Stack
              key={STORY_FRAMES_FIELD}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className={styles.deliverableRow}
            >
              <Body size="lg" className={styles.indented}>
                {getDeliverableLabel(STORY_FRAMES_FIELD, networkPrettyName)}
              </Body>
              <Stack
                direction="row"
                alignItems="center"
                className={styles.counterGroup}
              >
                <Stack
                  component="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  className={styles.counterButton}
                  onClick={() => onDeliverableChange(STORY_FRAMES_FIELD, -1)}
                  disabled={storyFramesCount <= MIN_STORY_FRAMES}
                >
                  −
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  className={styles.counterValue}
                >
                  <Body size="lg">{storyFramesCount}</Body>
                </Stack>
                <Stack
                  component="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  className={styles.counterButton}
                  onClick={() => onDeliverableChange(STORY_FRAMES_FIELD, 1)}
                >
                  +
                </Stack>
              </Stack>
            </Stack>,
          ];
        })}
        {inactiveDeliverables.length > 0 ? (
          <Stack gap="8px" alignItems="flex-start">
            <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
              {translate('Add new deliverables')}
            </Body>
            <Stack direction="row" flexWrap="wrap" gap="8px">
              {inactiveDeliverables.map((field) => (
                <Stack
                  key={field}
                  component="button"
                  type="button"
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  className={styles.chip}
                  onClick={() => onDeliverableChange(field, 1)}
                >
                  {getDeliverableLabel(field, networkPrettyName)}
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
      <Box className={styles.divider} />

      {hasPendingOffer ? (
        <Body size="sm" color={designSystemToken('semantic.fg.warning')}>
          {translate(
            'There is already a pending offer. Withdraw it before sending a new one.'
          )}
        </Body>
      ) : null}

      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        className={styles.acknowledgeRow}
        onClick={onAcknowledgeToggle}
      >
        <Checkbox checked={acknowledged} onChange={onAcknowledgeToggle} />
        <Body size="sm">
          {translate(
            'I acknowledge that this will send a negotiation offer to the creator'
          )}
        </Body>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        gap="12px"
        className={styles.buttonRow}
      >
        <Button
          mode="tinted"
          appearance="neutral"
          size="medium"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {translate('Cancel')}
        </Button>
        <Button
          mode="filled"
          appearance="neutral"
          size="medium"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {translate('Send Offer to Creator')}
        </Button>
      </Stack>
    </Stack>
  );
}

interface NegotiateModalProps {
  open: boolean;
  onClose: () => void;
  paidGigId: string | null;
  campaignId?: string;
  onSuccess?: () => void;
}

function NegotiateModal({
  open,
  onClose,
  paidGigId,
  campaignId,
  onSuccess,
}: NegotiateModalProps) {
  const {data: paidGig} = usePaidGigQuery(paidGigId);
  const {campaign, data: campaignData} = useCampaignQuery({
    campaignId,
    enabled: !!campaignId,
  });
  const createNegotiation = useCreateBrandNegotiation({paidGigId});

  const [offerCents, setOfferCents] = useState<number | null>(null);
  const [deliverables, setDeliverables] = useState<Record<
    DeliverableField,
    number
  > | null>(null);
  const [timePeriodMonths, setTimePeriodMonths] = useState<number | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const currentOfferCents = (paidGig?.cost ?? 0) * 100;

  const effectiveOfferCents = offerCents ?? currentOfferCents;

  const campaignTimePeriod = Number(
    campaign?.contentAgreementTimePeriodInMonths
  );
  const currentTimePeriod =
    paidGig?.contentAgreementTimePeriodInMonths ??
    (Number.isFinite(campaignTimePeriod) ? campaignTimePeriod : null);
  const effectiveTimePeriod = timePeriodMonths ?? currentTimePeriod;

  const currentDeliverables = useMemo((): Record<DeliverableField, number> => {
    if (!paidGig) {
      return ALL_DELIVERABLE_FIELDS.reduce(
        (acc, field) => ({...acc, [field]: 0}),
        {} as Record<DeliverableField, number>
      );
    }
    const result = ALL_DELIVERABLE_FIELDS.reduce(
      (acc, field) => ({
        ...acc,
        [field]:
          // STORY_FRAMES_FIELD's paidGig attribute name differs from its DELIVERABLE_KINDS key:
          // the API returns minimumChildContentSubmissionCount, not minimumNumberOfMixedMedia.
          field === STORY_FRAMES_FIELD
            ? (paidGig.minimumChildContentSubmissionCount ?? 0)
            : ((paidGig[field as keyof typeof paidGig] as number) ?? 0),
      }),
      {} as Record<DeliverableField, number>
    );
    // Mirrors Ember's addDeliverable action: when Story Uploads is active but the
    // server returns 0 frames (legacy data), default to the minimum so the field
    // is never blank on first open.
    if (result[STORY_UPLOADS_FIELD] > 0 && result[STORY_FRAMES_FIELD] === 0) {
      result[STORY_FRAMES_FIELD] = MIN_STORY_FRAMES;
    }
    return result;
  }, [paidGig]);

  const effectiveDeliverables = deliverables ?? currentDeliverables;

  // A margin is a share of the total, not a markup on the offer.
  const totalWithFees = campaignData
    ? Math.round(
        campaign.isMarginBased
          ? effectiveOfferCents / (1 - campaign.totalFeePercentage)
          : effectiveOfferCents * (1 + campaign.totalFeePercentage)
      )
    : null;

  const hasChanges = useMemo(() => {
    const offerChanged =
      offerCents !== null && offerCents !== currentOfferCents;
    const deliverablesChanged =
      deliverables !== null &&
      ALL_DELIVERABLE_FIELDS.some(
        (field) => deliverables[field] !== currentDeliverables[field]
      );
    const timePeriodChanged =
      timePeriodMonths !== null && timePeriodMonths !== currentTimePeriod;
    return offerChanged || deliverablesChanged || timePeriodChanged;
  }, [
    offerCents,
    currentOfferCents,
    deliverables,
    currentDeliverables,
    timePeriodMonths,
    currentTimePeriod,
  ]);

  const timePeriodValid =
    timePeriodMonths === null ||
    (timePeriodMonths >= 1 && timePeriodMonths <= MAX_TIME_PERIOD_MONTHS);

  const timePeriodError =
    timePeriodMonths !== null && !timePeriodValid
      ? translate(`Must be between 1 and ${MAX_TIME_PERIOD_MONTHS} months`)
      : null;

  const hasPendingOffer = paidGig?.brandNegotiation?.state === 'pending';

  const canSubmit =
    acknowledged &&
    hasChanges &&
    timePeriodValid &&
    !createNegotiation.isLoading &&
    !hasPendingOffer;

  const handleOfferChange = useCallback((value: string) => {
    if (value === '') {
      setOfferCents(null);
      return;
    }
    const dollars = parseFloat(value);
    if (Number.isNaN(dollars)) {
      return;
    }
    setOfferCents(Math.round(dollars * 100));
  }, []);

  const handleTimePeriodChange = useCallback((value: string) => {
    const months = parseInt(value, 10);
    if (value === '' || Number.isNaN(months)) {
      setTimePeriodMonths(null);
    } else {
      setTimePeriodMonths(months);
    }
  }, []);

  const handleDeliverableChange = useCallback(
    (field: DeliverableField, delta: number) => {
      setDeliverables((prev) => {
        const base = prev ?? {...currentDeliverables};
        const newVal = Math.max(0, (base[field] ?? 0) + delta);
        const next = {...base, [field]: newVal};
        if (field === STORY_UPLOADS_FIELD) {
          // Mirrors Ember's _resetMixedMediaRequiredSubKindCount and addDeliverable:
          // removing Story Uploads clears frames; adding it from zero initialises to minimum.
          if (newVal === 0) {
            next[STORY_FRAMES_FIELD] = 0;
          } else if ((base[STORY_UPLOADS_FIELD] ?? 0) === 0) {
            next[STORY_FRAMES_FIELD] = MIN_STORY_FRAMES;
          }
        }
        return next;
      });
    },
    [currentDeliverables]
  );

  const handleSubmit = useCallback(() => {
    if (!paidGigId || !canSubmit) {
      return;
    }

    // Mirrors Ember's payload shape: Min # of Story Frames is NOT a separate deliverable
    // kind — it is sent as requiredSubKindCount on the mixed_media_to_upload entry.
    const gigDeliverables = DELIVERABLE_FIELDS.map((field) => ({
      kind: DELIVERABLE_KINDS[field],
      requiredCount: effectiveDeliverables[field],
      ...(field === STORY_UPLOADS_FIELD && {
        requiredSubKindCount: effectiveDeliverables[STORY_FRAMES_FIELD],
      }),
    }));

    createNegotiation.mutate(
      {
        costInCents: effectiveOfferCents,
        paidGigId,
        gigDeliverables,
        contentAgreementTimePeriodInMonths: effectiveTimePeriod,
      },
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Negotiation offer sent')}
                subtitle=""
              />
            ),
          });
          setOfferCents(null);
          setDeliverables(null);
          setTimePeriodMonths(null);
          setAcknowledged(false);
          onSuccess?.();
          onClose();
        },
        onError: (error) => {
          const {isNegotiationNotAllowed, subtitle} =
            classifyNegotiationError(error);

          if (isNegotiationNotAllowed) {
            Sentry.captureMessage(
              'brand_negotiations 409: Negotiate was submitted despite the server forbidding it — a UI surface is missing the Negotiate gate.',
              {paidGigId, errorMessage: (error as Error)?.message}
            );
          }

          raiseFlashMessage({
            status: 'error',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Failed to send negotiation offer')}
                subtitle={subtitle}
              />
            ),
          });
        },
      }
    );
  }, [
    paidGigId,
    canSubmit,
    effectiveOfferCents,
    effectiveDeliverables,
    effectiveTimePeriod,
    createNegotiation,
    onSuccess,
    onClose,
  ]);

  const networkPrettyName = campaign?.campaignNetworkPrettyName;
  const allowedChipFields = getAllowedChipFields(networkPrettyName);

  const activeDeliverables = DELIVERABLE_FIELDS.filter(
    (field) => effectiveDeliverables[field] > 0
  );

  const inactiveDeliverables = DELIVERABLE_FIELDS.filter(
    (field) =>
      effectiveDeliverables[field] === 0 && allowedChipFields.has(field)
  );

  return (
    <Modal
      open={open}
      handleClose={createNegotiation.isLoading ? undefined : onClose}
      size="large"
      contentClassName={styles.modalContentSafeCenter}
      showCloseButton
    >
      <NegotiateModalContent
        isSubmitting={createNegotiation.isLoading}
        effectiveOfferCents={effectiveOfferCents}
        currentOfferCents={currentOfferCents}
        totalWithFees={totalWithFees}
        activeDeliverables={activeDeliverables}
        inactiveDeliverables={inactiveDeliverables}
        effectiveDeliverables={effectiveDeliverables}
        storyFramesCount={effectiveDeliverables[STORY_FRAMES_FIELD]}
        networkPrettyName={networkPrettyName}
        hasPendingOffer={hasPendingOffer}
        acknowledged={acknowledged}
        canSubmit={canSubmit}
        effectiveTimePeriod={effectiveTimePeriod}
        currentTimePeriod={currentTimePeriod}
        timePeriodError={timePeriodError}
        onOfferChange={handleOfferChange}
        onDeliverableChange={handleDeliverableChange}
        onTimePeriodChange={handleTimePeriodChange}
        onAcknowledgeToggle={() => setAcknowledged(!acknowledged)}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Modal>
  );
}

export default NegotiateModal;
